/**
 * frontend/src/utils/workStatus.js
 *
 * The one status vocabulary for tasks and activities (phase66), and the only
 * place its colours and wording are decided.
 *
 *   Pending       not started
 *   In Progress   being worked on
 *   Hold          paused — waiting on someone or something else
 *   Completed     finished
 *   Skipped       deliberately not done, and settled
 *
 * Every page used to keep its own fallback array, which is how tasks ended up
 * offering Blocked while activities did not. These are the FALLBACKS: a form
 * still reads its options from Masters first (useMaster), so an Admin can
 * relabel one without touching code. The VALUE is what the database stores
 * and what the CHECK constraint validates; the label is only what is read.
 *
 * SKIPPED IS NOT COMPLETED. It means the work was owed, the moment passed,
 * and it did not happen — the opposite outcome, not a second kind of success.
 * So there are three questions, with different answers:
 *
 *   isAchieved()  the work was DONE. Completed only. Progress bars and
 *                 completion rates ask this; never add Skipped to it.
 *   isSettled()   nobody is still working on it, whatever the outcome.
 *   isOpen()      still owed.
 *
 * Colours follow the Q-Lana palette. Red is reserved for error and danger, so
 * Hold — a legitimate, deliberate state — is Sunglow. Skipped is a darker
 * amber: it has to read as a miss rather than as a quiet grey nothing, but it
 * is a recorded outcome, not an error.
 */

export const WORK_STATUSES = ['Pending', 'In Progress', 'Hold', 'Completed', 'Skipped'];

export const STATUS_OPTIONS = WORK_STATUSES.map(v => ({ value: v, label: v }));

export const ACHIEVED_STATUSES = ['Completed'];
export const SETTLED_STATUSES  = ['Completed', 'Skipped'];
export const OPEN_STATUSES     = ['Pending', 'In Progress', 'Hold'];

export const isAchieved = (s) => ACHIEVED_STATUSES.includes(s);
export const isSettled  = (s) => SETTLED_STATUSES.includes(s);
export const isSkipped  = (s) => s === 'Skipped';
export const isOpen     = (s) => OPEN_STATUSES.includes(s);

/** Chip and column colours: { bg, border, text, dot }. */
export const STATUS_STYLE = {
  'Pending':     { dot:'#94a3b8', text:'#475569', bg:'#f1f5f9', border:'#cbd5e1' },
  'In Progress': { dot:'#2E539F', text:'#2E539F', bg:'rgba(46,83,159,.10)',  border:'#2E539F' },
  'Hold':        { dot:'#FFC849', text:'#92700f', bg:'rgba(255,200,73,.16)', border:'#FFC849' },
  'Completed':   { dot:'#5CC3BC', text:'#1f7a74', bg:'rgba(92,195,188,.14)', border:'#5CC3BC' },
  'Skipped':     { dot:'#b45309', text:'#b45309', bg:'rgba(180,83,9,.10)', border:'#d97706' },
};

export const statusStyle = (s) => STATUS_STYLE[s] || STATUS_STYLE['Pending'];

/** One line explaining each state, for a tooltip or a help row. */
export const STATUS_HELP = {
  'Pending':     'Not started yet.',
  'In Progress': 'Being worked on now.',
  'Hold':        'Paused — waiting on someone or something else.',
  'Completed':   'Finished.',
  'Skipped':     'Was owed and did not happen. Recorded as a miss, never as done.',
};

/** Kanban board order, left to right. */
export const BOARD_ORDER = ['Pending', 'In Progress', 'Hold', 'Completed', 'Skipped'];
